import { Pencil, Plus } from "lucide-react";
import { useState } from "react";
import { useAuth } from "react-oidc-context";
import { Link } from "react-router";
import { apiSend, useApi } from "../../api";
import type { PortalConfig } from "../../config";
import type { Lookups } from "../../types";
import { Modal } from "../ticket/Modal";
import { RangePicker, Tile, usd, useRange, type Range } from "./common";

interface Watchmaker {
  id: string; name: string; firstName: string | null; lastName: string | null; email: string | null; phone: string | null; active: boolean;
  creditServiceIds: string[];
  openNow: number; byStatus: { status: string; count: number }[]; overdue: number;
  paidTickets: number; revenue: number; expenses: number; profit: number; finished: number; avgDays: number | null;
}
interface Row { key: string; label: string; kind: "watchmaker" | "unassigned" | "shipping" | "insurance" | "tax"; paidTickets: number; revenue: number; expenses: number; profit: number }
interface Attribution { rows: Row[]; total: { paidTickets: number; revenue: number; expenses: number; profit: number } }
type Form = { businessName: string; firstName: string; lastName: string; email: string; phone: string; active: boolean; creditServiceIds: string[] };
const blank: Form = { businessName: "", firstName: "", lastName: "", email: "", phone: "", active: true, creditServiceIds: [] };
/** A watchmaker's share of paid tickets can be a fraction (1/n of a shared ticket): one decimal. */
const share = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1));
const ROW_HINT: Record<Row["kind"], string> = {
  watchmaker: "Service lines (after their discount) and expenses, split evenly among the watchmakers assigned to the ticket now",
  unassigned: "Tickets with no watchmaker assigned",
  shipping: "Shipping lines, and expenses from the supplier \"Shipping Cost\"; tickets: how many had shipping",
  insurance: "Insurance charged on the tickets; it costs nothing; tickets: how many had insurance",
  tax: "Sales tax collected: passed on, nobody's revenue, so no profit; tickets: how many were taxed",
};

/**
 * Watchmakers (owner, 2026-11-04; old Watchmakers page): each watchmaker (ticket location) with the work they hold
 * now, and for the range: tickets finished and average days, and who earned what on the tickets paid in full in the
 * range (owner-approved attribution rules, 2026-11-04; the totals equal the Dashboard's). Add and edit watchmakers
 * here, including the services a specialist is credited with when sharing a ticket.
 */
export function WatchmakersPage({ config }: { config: PortalConfig }) {
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";
  const [range, setRange] = useRange();
  const [reload, setReload] = useState(0);
  const [showInactive, setShowInactive] = useState(false);
  const { data, error } = useApi<{ range: Range; watchmakers: Watchmaker[]; attribution: Attribution }>(config, `/reports/watchmakers?from=${range.from}&to=${range.to}`, reload);
  const [editing, setEditing] = useState<string | "new" | null>(null);
  // Active services, for the specialist setting in the Add / Edit dialog (a retired one already ticked stays ticked).
  const lookups = useApi<Lookups>(config, editing ? "/lookups" : null);
  const [form, setForm] = useState<Form>(blank);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const all = data?.watchmakers ?? [];
  const list = all.filter((w) => showInactive || w.active || w.openNow > 0 || w.paidTickets > 0);
  const sum = (f: (w: Watchmaker) => number) => all.reduce((s, w) => s + f(w), 0);

  const open = (w: Watchmaker | null) => {
    setProblem(null);
    setEditing(w ? w.id : "new");
    setForm(w ? { businessName: w.name, firstName: w.firstName ?? "", lastName: w.lastName ?? "", email: w.email ?? "", phone: w.phone ?? "", active: w.active,
      creditServiceIds: w.creditServiceIds } : blank);
  };
  const save = async () => {
    setBusy(true); setProblem(null);
    try {
      await apiSend(config, token, editing === "new" ? "POST" : "PUT", editing === "new" ? "/watchmakers" : `/watchmakers/${editing}`, form);
      setEditing(null); setReload((n) => n + 1);
    } catch (e) { setProblem((e as Error).message); } finally { setBusy(false); }
  };
  const field = (k: keyof Omit<Form, "active" | "creditServiceIds">, label: string, type = "text") => (
    <label>{label}<input type={type} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} /></label>
  );

  return (
    <div className="report-page">
      <div className="report-head"><h2>Watchmakers</h2>
        <button type="button" className="primary" onClick={() => open(null)}><Plus size={15} /> Add watchmaker</button>
      </div>
      <RangePicker range={range} onChange={setRange}>
        <label className="check"><input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} /> Show inactive</label>
      </RangePicker>
      {error && <p className="error">{error.message}</p>}
      <div className="tiles">
        <Tile label="Open with watchmakers" value={sum((w) => w.openNow)} hint="Open tickets right now" />
        <Tile label="Overdue" value={sum((w) => w.overdue)} invert hint="Open tickets past their due date" />
        <Tile label="Finished in range" value={sum((w) => w.finished)} />
        <Tile label="Paid in full in range" value={data?.attribution.total.paidTickets ?? 0} hint="Tickets that became paid in full in the range" />
        <Tile label="Revenue in range" value={data?.attribution.total.revenue ?? 0} money hint="Invoice totals (incl. tax) of the tickets paid in full in the range" />
        <Tile label="Profit in range" value={data?.attribution.total.profit ?? 0} money hint="Revenue less sales tax and expenses, as on the Dashboard" />
      </div>
      {!data && !error && <p className="muted">Loading…</p>}
      {data && (
        <section className="panel">
          <h3>Who earned what <span className="muted small">— tickets paid in full in the range</span></h3>
          <table className="t-plain attrib">
            <thead><tr><th></th><th className="num">Paid tickets</th><th className="num">Revenue</th><th className="num">Expenses</th><th className="num">Profit</th></tr></thead>
            <tbody>{data.attribution.rows.map((r) => (
              <tr key={r.key} className={r.kind === "watchmaker" ? "" : "pass"} title={ROW_HINT[r.kind]}>
                <td>{r.label}</td><td className="num">{share(r.paidTickets)}</td><td className="num">{usd(r.revenue)}</td>
                <td className="num">{usd(r.expenses)}</td><td className={`num${r.profit < 0 ? " neg" : ""}`}>{r.kind === "tax" ? "—" : usd(r.profit)}</td>
              </tr>
            ))}</tbody>
            <tfoot><tr><td>Total</td><td className="num">{data.attribution.total.paidTickets}</td><td className="num">{usd(data.attribution.total.revenue)}</td>
              <td className="num">{usd(data.attribution.total.expenses)}</td><td className="num">{usd(data.attribution.total.profit)}</td></tr></tfoot>
          </table>
          <p className="muted small">A ticket counts once, in the range it became paid in full. Everything but shipping, insurance and tax is split evenly
            among the watchmakers assigned to the ticket now; a specialist sharing a ticket gets only their services. Hover a row for details.</p>
        </section>
      )}
      <div className="wm-grid">
        {list.map((w) => (
          <section key={w.id} className={`panel wm-card${w.active ? "" : " inactive"}`}>
            <header>
              <div><h3>{w.name}</h3>
                <p className="muted small">{[[w.firstName, w.lastName].filter(Boolean).join(" "), w.email, w.phone, w.active ? "" : "Inactive"].filter((x) => x && x !== w.name).join(" · ")}</p></div>
              <button type="button" className="icon-link" aria-label={`Edit ${w.name}`} onClick={() => open(w)}><Pencil size={15} /></button>
            </header>
            <div className="wm-now">
              <Link to={`/tickets?location=${w.id}&view=all`} className="wm-big">{w.openNow}<small>open now</small></Link>
              {w.overdue > 0 && <span className="pill warn">{w.overdue} overdue</span>}
              <div className="chips">{w.byStatus.map((s) => <span key={s.status} className="chip">{s.status} <strong>{s.count}</strong></span>)}</div>
            </div>
            <dl className="wm-stats">
              <div><dt>Paid tickets</dt><dd title="A shared ticket counts 1/n">{share(w.paidTickets)}</dd></div>
              <div><dt>Revenue</dt><dd>{usd(w.revenue, false)}</dd></div>
              <div><dt>Expenses</dt><dd>{usd(w.expenses, false)}</dd></div>
              <div><dt>Profit</dt><dd className={w.profit < 0 ? "neg" : ""}>{usd(w.profit, false)}</dd></div>
              <div><dt>Finished</dt><dd>{w.finished}</dd></div>
              <div><dt>Avg days</dt><dd>{w.avgDays ?? "—"}</dd></div>
            </dl>
          </section>
        ))}
        {data && !list.length && <p className="muted">No watchmakers yet.</p>}
      </div>
      {editing && (
        <Modal title={editing === "new" ? "Add watchmaker" : "Edit watchmaker"} onClose={() => setEditing(null)}>
          <form className="stack-form" onSubmit={(e) => { e.preventDefault(); void save(); }}>
            {field("businessName", "Name (shown as the ticket location)")}
            <div className="two">{field("firstName", "First name")}{field("lastName", "Last name")}</div>
            <div className="two">{field("email", "Email", "email")}{field("phone", "Phone", "tel")}</div>
            <label className="check"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active</label>
            <fieldset>
              <legend>When sharing a ticket, only credit these services</legend>
              <p className="muted small">Leave empty for a watchmaker who shares in everything. Working alone, they're credited with the whole ticket either way.</p>
              <div className="credit-list">
                {(lookups.data?.services ?? []).map((s) => (
                  <label key={s.id}><input type="checkbox" checked={form.creditServiceIds.includes(s.id)}
                    onChange={(e) => setForm({ ...form, creditServiceIds: e.target.checked ? [...form.creditServiceIds, s.id] : form.creditServiceIds.filter((x) => x !== s.id) })} />
                    {s.name}{s.category ? <span className="muted small"> · {s.category}</span> : null}</label>
                ))}
                {!lookups.data && <span className="muted small">Loading services…</span>}
              </div>
            </fieldset>
            {problem && <p className="error">{problem}</p>}
            <div className="btn-row between"><button type="button" onClick={() => setEditing(null)}>Cancel</button>
              <button type="submit" className="primary" disabled={busy || !form.businessName.trim()}>{busy ? "Saving…" : "Save"}</button></div>
          </form>
        </Modal>
      )}
    </div>
  );
}
